import { useEffect, useState } from 'react';
import { fetchAbbinatoreFacets, AbbinatoreFacets, FacetDomain } from '../api';

const EMPTY: AbbinatoreFacets = { marche: [], tipologie: [], categorie: [] };

/**
 * Opzioni dei filtri dell'abbinatore, ristrette in cascata dalla marca:
 * scelta BROTHER, la tendina successiva mostra solo le tipologie (o
 * categorie) che esistono davvero sotto BROTHER.
 *
 * Il parametro `dipendente` non è un extra: cambiando marca il valore
 * selezionato può sparire dalle opzioni, e lasciarlo attivo produce una
 * lista vuota senza che si capisca perché. Passandolo qui, l'azzeramento
 * sta in un posto solo invece che ricopiato in ogni sezione.
 */
export function useAbbinatoreFacets(
  domain: FacetDomain,
  brand: string | null,
  dipendente?: { value: string | null; onChange: (v: string | null) => void },
) {
  const [facets, setFacets] = useState<AbbinatoreFacets>(EMPTY);
  const [loading, setLoading] = useState(true);

  const onChange = dipendente?.onChange;
  const value = dipendente?.value ?? null;

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    fetchAbbinatoreFacets(domain, brand)
      .then(r => {
        if (cancel) return;
        setFacets(r);
        // Il valore selezionato non esiste più sotto questa marca: azzeralo
        // invece di lasciare un filtro che non matcha nulla.
        if (value && onChange) {
          const opts = domain === 'machines' ? r.tipologie : r.categorie;
          if (!opts.some(o => o.code === value)) onChange(null);
        }
      })
      .catch(() => { if (!cancel) setFacets(EMPTY); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
    // `value`/`onChange` di proposito fuori: il refetch dipende dalla marca,
    // non dal filtro dipendente — altrimenti l'azzeramento si rincorre.
  }, [domain, brand]);  // eslint-disable-line react-hooks/exhaustive-deps

  return {
    marche: facets.marche,
    tipologie: facets.tipologie,
    categorie: facets.categorie,
    loadingFacets: loading,
  };
}
